import { Report, REPORT_TYPE, ReportFilterField } from '@/types/reports'
import {
  APPLICATION,
  APPLICATION_TYPE,
  LICENSE_SCOPE,
  LicenseScope,
} from '@saasalerts/common/shared'
import { clientEnv } from '@/env-client'
import { BADGE_KIND } from '@saasalerts/ui'
import {
  faShieldHalved,
  faLink,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'
const getServerConfig = (key: string) =>
  (process.env[key] ??
    (process.env.SKIP_ENV_VALIDATION
      ? '__SECRET_MANAGER__'
      : undefined)) as string

// Client Side Variables
export const FIREBASE_API_KEY = clientEnv.firebase.apiKey
export const FIREBASE_AUTH_DOMAIN = clientEnv.firebase.authDomain
export const FIREBASE_DATABASE_URL = clientEnv.firebase.databaseUrl
export const FIREBASE_PROJECT_ID = clientEnv.firebase.projectId
export const K1_AUTHORIZATION_ENDPOINT = clientEnv.k1.authorizationEndpoint
export const K1_CLIENT_ID = clientEnv.k1.clientId
export const K1_CLIENT_IDS = new Map<LicenseScope, string | undefined>([
  [LICENSE_SCOPE.SA, clientEnv.k1.clientIdSa],
  [LICENSE_SCOPE.KMDR, clientEnv.k1.clientIdKmdr],
  [LICENSE_SCOPE.KSIEM, clientEnv.k1.clientIdKsiem],
])
export const SESSION_TIMEOUT_IDLE_SECONDS = 3600
export const SESSION_TIMEOUT_IDLE_PROMPT_SECONDS = 60
export const GAE_RUNTIME = clientEnv.app.gaeRuntime

// Server side variables
export const FIREBASE_CLIENT_EMAIL = getServerConfig('FIREBASE_CLIENT_EMAIL')
export const FIREBASE_PRIVATE_KEY = getServerConfig('FIREBASE_PRIVATE_KEY')

export const MANAGE_PROXY_SERVICE_ACCOUNT_CLIENT_EMAIL = getServerConfig(
  'MANAGE_PROXY_SERVICE_ACCOUNT_CLIENT_EMAIL'
)
export const MANAGE_PROXY_SERVICE_ACCOUNT_PRIVATE_KEY = getServerConfig(
  'MANAGE_PROXY_SERVICE_ACCOUNT_PRIVATE_KEY'
)

export const SERVER_API_HOST = clientEnv.api.serverHost
export const REDIS_HOST = getServerConfig('REDIS_HOST')
export const REDIS_PORT = getServerConfig('REDIS_PORT')
export const ENVIRONMENT = clientEnv.app.environment

export const SAAS_ALERTS_LOGO = '/images/logo.svg'
export const SAAS_ALERTS_COLOR = 'brand'

export const JWT_SECRET = getServerConfig('JWT_SECRET')
export const CRYPTR_SECRET = getServerConfig('CRYPTR_SECRET')

export const SHARE_PATH = clientEnv.app.sharePath

export const SENTRY_DSN = clientEnv.sentry.dsn
export const SENTRY_ENVIRONMENT = clientEnv.sentry.environment

export const FILE_SHARE_DOES_NOT_EXPIRE = 'Expiration date not reported'

export const MANAGE_PROXY_API_KEY = getServerConfig('MANAGE_PROXY_API_KEY')
export const MANAGE_GLOBAL_SECRET_KEY = getServerConfig(
  'MANAGE_GLOBAL_SECRET_KEY'
)
export const MANAGE_CLIENT_APP_HOST = clientEnv.manage.clientAppHost

export const MS_GRAPH_API_URL_V1 = 'https://graph.microsoft.com/v1.0'
export const MS_GRAPH_API_URL_BETA = 'https://graph.microsoft.com/beta'

const PUBLIC_ROUTES: string[] = [
  '/share/reports/executive-summary',
  '/share/reports/saas-risk-report',
  '/share/reports/saas-cyber-assessment',
  '/share/reports/critical-customer-alerts',
  '/share/reports/medium-customer-alerts',
  '/share/reports/low-customer-alerts',
  '/share/reports/user-detail',
  '/share/reports/externally-shared-files',
  '/share/reports/file-share-events',
  '/share/reports/user-list',
  '/share/reports/mfa-settings-report',
  '/share/reports/billing-details',
  '/share/reports/foreign-applications',
  '/share/reports/organization-vulnerability-assessment',
  '/share/reports/respond-rule-trigger-report',
  '/share/reports/threat-mitigation',
  '/share/reports/device-mapping-list',
  '/share/reports/psa-account',
  '/share/reports/psa-organization-mapping',
  '/share/reports/fortify-actions-audit',
  '/share/reports/enterprise-applications-details',
  '/oidc/callback/k1',
  '/share/reports/account-mailbox-forwarding-rules-report',
  '/share/reports/stale-devices',
  '/share/reports/fortify-snapshot-details',
]

export const LIST_OF_REPORTS: Report[] = [
  {
    id: 'executive-summary',
    name: 'Executive Summary',
    href: '/reports/executive-summary',
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Executive Reports',
    domainScope: [LICENSE_SCOPE.KSIEM, LICENSE_SCOPE.KMDR],
  },
  {
    id: 'saas-cyber-assessment',
    name: 'SaaS Cyber Assessment',
    href: '/reports/saas-cyber-assessment',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Organization Reports',
  },
  {
    id: 'organization-vulnerability-assessment',
    name: 'Organization Vulnerability Assessment',
    href: '/reports/organization-vulnerability-assessment',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [
      ReportFilterField.ORGANIZATION,
      ReportFilterField.FORTIFY_BENCHMARK_TYPE,
    ],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
      {
        title: 'Fortify',
        color: BADGE_KIND.SAFFRON,
        icon: faShieldHalved,
        description:
          'This report is only valid for an Organization with an active Fortify connection.',
      },
    ],
  },
  {
    id: 'threat-mitigation',
    name: 'Threat Mitigation',
    href: '/reports/threat-mitigation',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [
      ReportFilterField.ORGANIZATION,
      ReportFilterField.DATE_RANGE,
      ReportFilterField.FORTIFY_BENCHMARK_TYPE,
    ],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
      {
        title: 'Fortify',
        color: BADGE_KIND.SAFFRON,
        icon: faShieldHalved,
        description:
          'This report is only valid for an Organization with an active Fortify connection.',
      },
    ],
  },
  {
    id: 'saas-risk-report',
    name: 'SaaS Risk Report',
    href: '/reports/saas-risk-report',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Organization Reports',
  },
  {
    id: 'critical-customer-alerts',
    name: 'Critical Alerts',
    href: '/reports/critical-customer-alerts',
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'MSP Only Reports',
  },
  {
    id: 'customer-alerts',
    name: 'Medium Alerts',
    href: '/reports/medium-customer-alerts',
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'MSP Only Reports',
  },
  {
    id: 'user-detail',
    name: 'Account Detail',
    href: '/reports/user-detail',
    filters: [ReportFilterField.ACCOUNT_EMAIL, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: false,
    group: 'Organization Reports',
  },
  {
    id: 'customer-logged-events',
    name: 'Low Alerts',
    href: '/reports/low-customer-alerts',
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'MSP Only Reports',
  },
  {
    id: 'billing-details',
    name: 'Billing Details',
    href: '/reports/billing-details',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.DATE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: true,
    group: 'MSP Only Reports',
  },
  {
    id: 'foreign-applications-details',
    name: 'Foreign Applications Details (Shadow IT)',
    href: '/reports/foreign-applications',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: true,
    group: 'MSP Only Reports',
  },
  {
    id: 'psa-account',
    name: 'PSA Account Sync Report',
    subTitle: 'List of billable account sync activity for PSA connections',
    href: '/reports/psa-account',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.DATE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'MSP Only Reports',
  },
  {
    id: 'psa-organization-mapping',
    name: 'PSA Organization Mapping Report',
    subTitle: 'Organization mapping and status information for PSA connections',
    href: '/reports/psa-organization-mapping',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'MSP Only Reports',
  },
  {
    id: 'enterprise-applications-details',
    name: 'Enterprise Applications Details',
    href: '/reports/enterprise-applications-details',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'Organization Reports',
  },
  {
    id: 'stale-devices',
    name: 'Stale Devices Report',
    href: '/reports/stale-devices',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION],
    reportType: REPORT_TYPE.OPERATIONAL,
    printable: false,
    group: 'Organization Reports',
  },
  {
    id: 'externally-shared-files',
    name: 'Externally Shared Files',
    href: '/reports/externally-shared-files',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: false,
    group: 'Organization Reports',
  },
  {
    id: 'file-share-events',
    name: 'File Share Events',
    href: '/reports/file-share-events',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: false,
    group: 'Organization Reports',
  },
  {
    id: 'user-list',
    name: 'Account List',
    href: '/reports/user-list',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: false,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
    ],
  },
  {
    id: 'device-mapping-list',
    name: 'Device Mapping List',
    href: '/reports/device-mapping-list',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: true,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Unify',
        color: BADGE_KIND.SAFFRON,
        icon: faLink,
        description:
          'This report is only valid for an Organization with an active Unify connection.',
      },
    ],
  },
  {
    id: 'fortify-actions-audit-report',
    name: 'Fortify Actions Audit Report',
    href: '/reports/fortify-actions-audit',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: false,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
      {
        title: 'Fortify',
        color: BADGE_KIND.SAFFRON,
        icon: faShieldHalved,
        description:
          'This report is only valid for an Organization with an active Fortify connection.',
      },
    ],
  },
  {
    id: 'fortify-snapshot-details',
    name: 'Fortify Snapshot Details Report',
    href: '/reports/fortify-snapshot-details',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.FORTIFY_SNAPSHOT],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: false,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
      {
        title: 'Fortify',
        color: BADGE_KIND.SAFFRON,
        icon: faShieldHalved,
        description:
          'This report is only valid for an Organization with an active Fortify connection.',
      },
    ],
  },
  {
    id: 'mfa-settings-report',
    name: 'MFA Settings Report',
    href: '/reports/mfa-settings-report',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.APPLICATION, ReportFilterField.ORGANIZATION],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: false,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations with a P1+ license. Blocked and Guest accounts do not appear on this report.',
      },
      {
        title: 'Google Workspace',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Google Workspace Organizations. Blocked and Guest accounts do not appear on this report.',
      },
    ],
  },
  {
    id: 'account-mailbox-forwarding-rules-report',
    name: 'Account Mailbox Forwarding Rule Report',
    href: '/reports/account-mailbox-forwarding-rules-report',
    domainScope: [LICENSE_SCOPE.SA, LICENSE_SCOPE.KSIEM],
    filters: [ReportFilterField.ORGANIZATION],
    reportType: REPORT_TYPE.CONFIGURATION,
    printable: false,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations.',
      },
    ],
  },
  {
    id: 'respond-rule-trigger-report',
    name: 'Respond Rule Trigger Report',
    href: '/reports/respond-rule-trigger-report',
    filters: [ReportFilterField.ORGANIZATION, ReportFilterField.DATE_RANGE],
    reportType: REPORT_TYPE.ORGANIZATION,
    printable: true,
    group: 'Organization Reports',
    tags: [
      {
        title: 'Microsoft',
        color: BADGE_KIND.BLUE,
        description:
          'This report is only valid for Microsoft 365 Organizations with an active Respond connection.',
      },
    ],
  },
]

export const ALL_THEMES = `theme-red theme-blue theme-green theme-pink theme-indigo theme-purple theme-yellow theme-brand`

export const isPublicRoute = (path: string): boolean => {
  return PUBLIC_ROUTES.includes(path)
}

export const getThemeClass = (theme = 'brand'): string => {
  return `theme-${theme}`
}

export const isLoginPage = (path: string): boolean => {
  return path === '/auth'
}

export const COLLECTIONS = {
  SCHEDULED_REPORTS: 'scheduledReports',
  REPORT_API_KEYS: 'reportApiKeys',
  IMPORTED_USERS: 'importedUsers',
  CUSTOMERS: 'customers',
  PARTNER_USERS: 'users',
  PSA_REPORTS: 'psaReports',
  PSA_CONNECTIONS: 'psaConnections',
}
const ROCKETCYBER_IMAGE_PREFIX = '/images/application/rocketcyber/'

export const PRODUCT_IMAGES = {
  SALES_FORCE: '/images/application/salesforce.png',
  MS: '/images/application/microsoft.png',
  G_SUITE: '/images/application/gsuite.png',
  DROP_BOX: '/images/application/dropbox.png',
  SLACK: '/images/application/slack.png',
  IT_GLUE: '/images/application/itglue.png',
  NINJA_ONE: '/images/application/ninjaone.png',
  CONNECTWISE_AUTOMATE: '/images/application/connectwise.png',
  KASEYA_VSA: '/images/application/vsakaseya.png',
  SAAS_ALERTS_RESPOND: '/images/application/saas-alerts-respond.png',
  SAAS_ALERTS_REPORTS: '/images/application/saas-alerts-reports.png',
  SAAS_ALERTS_MANAGE: '/images/application/saas-alerts-manage.png',
  SAAS_ALERTS_FORTIFY: '/images/application/saas-alerts-fortify.png',
  OKTA: '/images/application/okta.png',
  KASEYA_ONE_SSO: '/images/application/kaseyaone_sso.png',
  ROCKETCYBER: '/images/application/rocketcyber.png',
  DATTO_EDR: '/images/application/dattoedr.png',
  DATTO_RMM: '/images/application/dattormm.png',
  SYNCRO_RMM: '/images/application/syncro_rmm.png',
  DUO: '/images/application/duo.png',
  EGNYTE: '/images/application/egnyte.png',
  HUDU: '/images/application/hudu.png',
  MS_DEFENDER: '/images/application/ms_defender.png',
  N_ABLE_N_CENTRAL: '/images/application/nable_ncentral.png',
  SENTINEL_ONE: '/images/application/sentinel_one.png',
  CLOUDTRAIL: '/images/application/cloudtrail.png',
  ZOOM: '/images/application/zoom.png',

  ACTIVE_DIRECTORY_MONITOR_AND_SYNC: `${ROCKETCYBER_IMAGE_PREFIX}activedirectorymonitorandsync.png`,
  ADVANCED_BREACH_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}advancedbreachdetection.png`,
  BARRACUDA_ESS_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}barracudaessmonitor.png`,
  BARRACUDA_SENTINEL_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}barracudasentinelmonitor.png`,
  BITDEFENDER_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}bitdefendermonitor.png`,
  CRYPTO_MINING_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}cryptominingdetection.png`,
  CYBER_TERRORIST_NETWORK_CONNECTIONS: `${ROCKETCYBER_IMAGE_PREFIX}cyberterroristnetworkconnections.png`,
  CYLANCE_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}cylancemonitor.png`,
  DARK_WEB_ID_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}darkwebidmonitor.png`,
  DATTO_EDR_AV_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}dattoedravmonitor.png`,
  DATTO_RANSOMWARE_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}dattoransomwaredetection.png`,
  DEEP_INSTINCT_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}deepinstinctmonitor.png`,
  DEFENDER_FOR_BUSINESS: `${ROCKETCYBER_IMAGE_PREFIX}defenderforbusiness.png`,
  DEFENDER_MANAGER: `${ROCKETCYBER_IMAGE_PREFIX}defendermanager.png`,
  DNS_FILTER_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}dnsfiltermonitor.png`,
  ENDPOINT_EVENT_LOG_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}endpointeventlogmonitor.png`,
  FIREWALL_LOG_ANALYZER: `${ROCKETCYBER_IMAGE_PREFIX}firewallloganalyzer.png`,
  GRAPHUS: `${ROCKETCYBER_IMAGE_PREFIX}graphus.png`,
  IOC_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}iocdetection.png`,
  IRONSCALES_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}ironscalesmonitor.png`,
  LOG4J_DETECTOR: `${ROCKETCYBER_IMAGE_PREFIX}log4jdetector.png`,
  MALICIOUS_FILE_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}maliciousfiledetection.png`,
  MICROSOFT_EXCHANGE_THREAT_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}microsoftexchangethreatdetection.png`,
  OFFICE_365_LOGIN_ANALYZER: `${ROCKETCYBER_IMAGE_PREFIX}office365loginanalyzer.png`,
  OFFICE_365_LOG_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}office365logmonitor.png`,
  OFFICE_365_RISK_DETECTION: `${ROCKETCYBER_IMAGE_PREFIX}office365riskdetection.png`,
  OFFICE_365_SECURE_SCORE: `${ROCKETCYBER_IMAGE_PREFIX}office365securescore.png`,
  PASSLY_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}passlymonitor.png`,
  PRINT_NIGHTMARE_HUNT: `${ROCKETCYBER_IMAGE_PREFIX}printnightmarehunt.png`,
  PWND_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}pwndmonitor.png`,
  SENTINELONE_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}sentinelonemonitor.png`,
  SOPHOS_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}sophosmonitor.png`,
  SUSPICIOUS_NETWORK_SERVICES: `${ROCKETCYBER_IMAGE_PREFIX}suspiciousnetworkservices.png`,
  SUSPICIOUS_TOOLS: `${ROCKETCYBER_IMAGE_PREFIX}suspicioustools.png`,
  SYSLOG_COLLECTOR: `${ROCKETCYBER_IMAGE_PREFIX}syslogcollector.png`,
  VSA_THREAT_HUNT: `${ROCKETCYBER_IMAGE_PREFIX}vsathreathunt.png`,
  VULSCAN_COLLECTOR: `${ROCKETCYBER_IMAGE_PREFIX}vulscancollector.png`,
  WEBROOT_MONITOR: `${ROCKETCYBER_IMAGE_PREFIX}webrootmonitor.png`,
}

export const WEBHOOK_EVENT = {
  REPORTS_USER_LOGGED_IN: 'reports.user.logged.in',
  REPORTS_USER_LOGGED_OUT: 'reports.user.logged.out',
  REPORTS_REPORT_VIEWED: 'reports.report.viewed',
  REPORTS_REPORT_SHARED: 'reports.report.shared',
  REPORTS_REPORT_SCHEDULED: 'reports.report.scheduled',
  REPORTS_SETTINGS_UPDATED: 'reports.settings.updated',
} as const

export const MANAGE = {
  HEADER_MANAGE_API_KEY: 'api_key',
  HEADER_MANAGE_EVENT_NAME: 'x-saasalerts-event-name',
} as const

export const ReportSearchIncludeFields = [
  'user.name',
  'user.fullName',
  'alertStatus',
  'jointDesc',
  'location.ll',
  'location.country',
  'location.city',
  'location.region',
  'location.ip_owner',
  'time',
  'ip',
  'product.name',
  'customer.id',
  'customer.name',
]

export const MFA_REPORT_APPLICATIONS = [
  {
    id: APPLICATION_TYPE.MS,
    name: APPLICATION.MS.name,
  },
  {
    id: APPLICATION_TYPE.G_SUITE,
    name: APPLICATION.G_SUITE.name,
  },
]

export const AllowedExtensions = ['jpg', 'jpeg', 'png'] // List of allowed image file extensions
